<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import InformationalBroadcastsAPI from 'dashboard/api/informationalBroadcasts';

const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const PER_PAGE = 25;
const DAY_KEYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const { t } = useI18n();
const dayLabels = [
  t('INFORMATIONAL_BROADCAST.DAYS.SUN'),
  t('INFORMATIONAL_BROADCAST.DAYS.MON'),
  t('INFORMATIONAL_BROADCAST.DAYS.TUE'),
  t('INFORMATIONAL_BROADCAST.DAYS.WED'),
  t('INFORMATIONAL_BROADCAST.DAYS.THU'),
  t('INFORMATIONAL_BROADCAST.DAYS.FRI'),
  t('INFORMATIONAL_BROADCAST.DAYS.SAT'),
];
const exclusionLabels = {
  conversation_resolved: t(
    'INFORMATIONAL_BROADCAST.EXCLUSION_REASONS.conversation_resolved'
  ),
  conversation_archived: t(
    'INFORMATIONAL_BROADCAST.EXCLUSION_REASONS.conversation_archived'
  ),
  contact_blocked: t(
    'INFORMATIONAL_BROADCAST.EXCLUSION_REASONS.contact_blocked'
  ),
  cannot_reply: t('INFORMATIONAL_BROADCAST.EXCLUSION_REASONS.cannot_reply'),
  invalid_inbox: t('INFORMATIONAL_BROADCAST.EXCLUSION_REASONS.invalid_inbox'),
  duplicate_conversation: t(
    'INFORMATIONAL_BROADCAST.EXCLUSION_REASONS.duplicate_conversation'
  ),
};
const preview = ref(null);
const loading = ref(true);
const detailsOpen = ref(false);
const page = ref(1);
const messages = ref([{ id: 1, value: '' }]);
const nextMessageId = ref(2);
const attachment = ref(null);
const minInterval = ref(30);
const maxInterval = ref(90);
const allowedDays = ref([1, 2, 3, 4, 5]);
const windowStart = ref('08:00');
const windowEnd = ref('18:00');
const estimate = ref(null);
const starting = ref(false);
const campaignId = ref(null);
const campaign = ref(null);
let estimateTimer;
let statusTimer;

const excludedTotal = computed(() =>
  Object.values(preview.value?.excluded || {}).reduce(
    (total, count) => total + Number(count || 0),
    0
  )
);

const validMessages = computed(() =>
  messages.value.map(item => item.value.trim()).filter(Boolean)
);

const isValid = computed(
  () =>
    Number(preview.value?.eligibleCount || 0) > 0 &&
    validMessages.value.length > 0 &&
    minInterval.value > 0 &&
    maxInterval.value >= minInterval.value &&
    allowedDays.value.length > 0 &&
    windowStart.value < windowEnd.value
);

const progressPercent = computed(() =>
  Math.round(Number(campaign.value?.progress || 0) * 100)
);

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || fallback;

const loadPreview = async (nextPage = page.value) => {
  loading.value = true;
  try {
    const { data } = await InformationalBroadcastsAPI.preview({
      page: nextPage,
      perPage: PER_PAGE,
    });
    preview.value = data.preview;
    page.value = nextPage;
  } catch (error) {
    useAlert(errorMessage(error, t('INFORMATIONAL_BROADCAST.ERRORS.PREVIEW')));
  } finally {
    loading.value = false;
  }
};

const toggleDay = day => {
  allowedDays.value = allowedDays.value.includes(day)
    ? allowedDays.value.filter(value => value !== day)
    : [...allowedDays.value, day].sort();
};

const addMessage = () => {
  messages.value.push({ id: nextMessageId.value, value: '' });
  nextMessageId.value += 1;
};

const removeMessage = index => {
  messages.value.splice(index, 1);
};

const onFileChange = event => {
  const file = event.target.files?.[0] || null;
  if (file && file.size > MAX_ATTACHMENT_BYTES) {
    event.target.value = '';
    attachment.value = null;
    useAlert(t('INFORMATIONAL_BROADCAST.ERRORS.ATTACHMENT_SIZE'));
    return;
  }
  attachment.value = file;
};

const fileToBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result).split(',', 2)[1] || '');
    reader.readAsDataURL(file);
  });

const estimatePayload = () => ({
  recipient_count: Number(preview.value?.eligibleCount || 0),
  min_interval_seconds: Number(minInterval.value),
  max_interval_seconds: Number(maxInterval.value),
  timezone: 'America/Sao_Paulo',
  allowed_days: allowedDays.value,
  window_start: windowStart.value,
  window_end: windowEnd.value,
});

const refreshEstimate = async () => {
  if (!isValid.value) {
    estimate.value = null;
    return;
  }
  try {
    const { data } =
      await InformationalBroadcastsAPI.estimate(estimatePayload());
    estimate.value = data;
  } catch {
    estimate.value = null;
  }
};

const startCampaign = async () => {
  if (!isValid.value || starting.value) return;
  starting.value = true;
  try {
    const file = attachment.value;
    const attachmentPayload = file
      ? {
          name: file.name,
          mime: file.type || 'application/octet-stream',
          data_base64: await fileToBase64(file),
        }
      : null;
    const { data } = await InformationalBroadcastsAPI.create({
      message_variants: validMessages.value,
      min_interval_seconds: Number(minInterval.value),
      max_interval_seconds: Number(maxInterval.value),
      timezone: 'America/Sao_Paulo',
      allowed_days: allowedDays.value,
      window_start: windowStart.value,
      window_end: windowEnd.value,
      attachment: attachmentPayload,
    });
    campaignId.value = data.campaignId;
    campaign.value = null;
    useAlert(t('INFORMATIONAL_BROADCAST.STARTED'));
  } catch (error) {
    useAlert(errorMessage(error, t('INFORMATIONAL_BROADCAST.ERRORS.START')));
  } finally {
    starting.value = false;
  }
};

const pollCampaign = async () => {
  if (!campaignId.value) return;
  try {
    const { data } = await InformationalBroadcastsAPI.show(campaignId.value);
    campaign.value = data.campaign;
    if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(data.campaign?.status)) {
      window.clearInterval(statusTimer);
      statusTimer = undefined;
    }
  } catch (error) {
    window.clearInterval(statusTimer);
    statusTimer = undefined;
    useAlert(errorMessage(error, t('INFORMATIONAL_BROADCAST.ERRORS.STATUS')));
  }
};

watch(
  [preview, minInterval, maxInterval, allowedDays, windowStart, windowEnd],
  () => {
    window.clearTimeout(estimateTimer);
    estimateTimer = window.setTimeout(refreshEstimate, 300);
  },
  { deep: true }
);

watch(campaignId, value => {
  window.clearInterval(statusTimer);
  if (!value) return;
  pollCampaign();
  statusTimer = window.setInterval(pollCampaign, 5000);
});

onMounted(() => loadPreview(1));
onBeforeUnmount(() => {
  window.clearTimeout(estimateTimer);
  window.clearInterval(statusTimer);
});
</script>

<template>
  <div class="flex-1 h-full overflow-auto bg-n-surface-1">
    <div class="w-full max-w-6xl p-6 mx-auto space-y-6">
      <header>
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('INFORMATIONAL_BROADCAST.TITLE') }}
        </h1>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('INFORMATIONAL_BROADCAST.SUBTITLE') }}
        </p>
      </header>

      <section
        class="p-5 space-y-4 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h2 class="font-medium text-n-slate-12">
            {{ t('INFORMATIONAL_BROADCAST.RECIPIENTS.TITLE') }}
          </h2>
          <Button
            :label="t('INFORMATIONAL_BROADCAST.REFRESH')"
            icon="i-lucide-refresh-cw"
            slate
            outline
            :is-loading="loading"
            @click="loadPreview(1)"
          />
        </div>

        <div v-if="loading && !preview" class="flex justify-center py-8">
          <Spinner />
        </div>
        <div v-else class="grid gap-3 sm:grid-cols-3">
          <div class="p-4 rounded-lg bg-n-alpha-2">
            <strong class="block text-2xl text-n-slate-12">
              {{ preview?.eligibleCount || 0 }}
            </strong>
            <span class="text-sm text-n-slate-11">
              {{ t('INFORMATIONAL_BROADCAST.RECIPIENTS.ELIGIBLE') }}
            </span>
          </div>
          <div class="p-4 rounded-lg bg-n-alpha-2">
            <strong class="block text-2xl text-n-slate-12">
              {{ excludedTotal }}
            </strong>
            <span class="text-sm text-n-slate-11">
              {{ t('INFORMATIONAL_BROADCAST.RECIPIENTS.EXCLUDED') }}
            </span>
          </div>
          <div class="p-4 rounded-lg bg-n-alpha-2">
            <strong class="block text-2xl text-n-slate-12">
              {{ preview?.totalConversations || 0 }}
            </strong>
            <span class="text-sm text-n-slate-11">
              {{ t('INFORMATIONAL_BROADCAST.RECIPIENTS.ANALYZED') }}
            </span>
          </div>
        </div>

        <Button
          :label="
            detailsOpen
              ? t('INFORMATIONAL_BROADCAST.HIDE_DETAILS')
              : t('INFORMATIONAL_BROADCAST.SHOW_DETAILS')
          "
          slate
          ghost
          @click="detailsOpen = !detailsOpen"
        />

        <div v-if="detailsOpen && preview" class="grid gap-5 lg:grid-cols-2">
          <div>
            <h3 class="mb-2 text-sm font-medium text-n-slate-12">
              {{ t('INFORMATIONAL_BROADCAST.RECIPIENTS.EXCLUSIONS') }}
            </h3>
            <dl class="space-y-1 text-sm text-n-slate-11">
              <div
                v-for="(count, reason) in preview.excluded"
                :key="reason"
                class="flex justify-between gap-3"
              >
                <dt>
                  {{ exclusionLabels[reason] || reason }}
                </dt>
                <dd>{{ count }}</dd>
              </div>
            </dl>
          </div>
          <div>
            <h3 class="mb-2 text-sm font-medium text-n-slate-12">
              {{ t('INFORMATIONAL_BROADCAST.RECIPIENTS.TITLE') }}
            </h3>
            <ul class="overflow-hidden border rounded-lg border-n-weak">
              <li
                v-for="recipient in preview.recipients"
                :key="recipient.contactId"
                class="px-3 py-2 text-sm border-b last:border-b-0 border-n-weak text-n-slate-12"
              >
                {{
                  recipient.name ||
                  t('INFORMATIONAL_BROADCAST.CONTACT_FALLBACK', {
                    id: recipient.contactId,
                  })
                }}
                <span class="text-n-slate-10">
                  {{
                    t('INFORMATIONAL_BROADCAST.CONVERSATION', {
                      id: recipient.conversationId,
                    })
                  }}
                </span>
              </li>
            </ul>
            <div class="flex items-center justify-between mt-3">
              <Button
                :label="t('INFORMATIONAL_BROADCAST.PREVIOUS')"
                slate
                outline
                :disabled="page <= 1"
                @click="loadPreview(page - 1)"
              />
              <span class="text-sm text-n-slate-11">
                {{ t('INFORMATIONAL_BROADCAST.PAGE', { page }) }}
              </span>
              <Button
                :label="t('INFORMATIONAL_BROADCAST.NEXT')"
                slate
                outline
                :disabled="preview.recipients.length < preview.perPage"
                @click="loadPreview(page + 1)"
              />
            </div>
          </div>
        </div>
      </section>

      <section
        class="p-5 space-y-4 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <h2 class="font-medium text-n-slate-12">
          {{ t('INFORMATIONAL_BROADCAST.MESSAGE.TITLE') }}
        </h2>
        <div
          v-for="(message, index) in messages"
          :key="message.id"
          class="flex items-start gap-2"
        >
          <textarea
            v-model="message.value"
            rows="4"
            maxlength="4000"
            class="w-full px-3 py-2 border rounded-lg resize-y border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-n-brand"
            :aria-label="
              t('INFORMATIONAL_BROADCAST.MESSAGE.VARIATION', {
                number: index + 1,
              })
            "
            :placeholder="
              index === 0
                ? t('INFORMATIONAL_BROADCAST.MESSAGE.PLACEHOLDER')
                : t('INFORMATIONAL_BROADCAST.MESSAGE.VARIATION', {
                    number: index + 1,
                  })
            "
          />
          <Button
            v-if="messages.length > 1"
            icon="i-lucide-trash-2"
            ruby
            ghost
            :aria-label="t('INFORMATIONAL_BROADCAST.MESSAGE.REMOVE')"
            @click="removeMessage(index)"
          />
        </div>
        <Button
          :label="t('INFORMATIONAL_BROADCAST.MESSAGE.ADD_VARIATION')"
          icon="i-lucide-plus"
          slate
          outline
          @click="addMessage"
        />
        <label class="block text-sm text-n-slate-12">
          <span class="block mb-1">
            {{ t('INFORMATIONAL_BROADCAST.MESSAGE.ATTACHMENT') }}
          </span>
          <input
            type="file"
            class="block w-full text-sm text-n-slate-11 file:mr-3 file:rounded-lg file:border-0 file:bg-n-alpha-2 file:px-3 file:py-2 file:text-n-slate-12"
            @change="onFileChange"
          />
          <span class="block mt-1 text-xs text-n-slate-10">
            {{ t('INFORMATIONAL_BROADCAST.MESSAGE.ATTACHMENT_HINT') }}
          </span>
        </label>
      </section>

      <section
        class="grid gap-5 p-5 border rounded-xl md:grid-cols-2 border-n-weak bg-n-solid-2"
      >
        <div class="space-y-3">
          <h2 class="font-medium text-n-slate-12">
            {{ t('INFORMATIONAL_BROADCAST.INTERVAL.TITLE') }}
          </h2>
          <label class="block text-sm text-n-slate-12">
            {{ t('INFORMATIONAL_BROADCAST.INTERVAL.MINIMUM') }}
            <input
              v-model.number="minInterval"
              type="number"
              min="1"
              max="86400"
              class="w-full px-3 py-2 mt-1 border rounded-lg border-n-weak bg-n-alpha-1"
            />
          </label>
          <label class="block text-sm text-n-slate-12">
            {{ t('INFORMATIONAL_BROADCAST.INTERVAL.MAXIMUM') }}
            <input
              v-model.number="maxInterval"
              type="number"
              min="1"
              max="86400"
              class="w-full px-3 py-2 mt-1 border rounded-lg border-n-weak bg-n-alpha-1"
            />
          </label>
        </div>
        <div class="space-y-3">
          <h2 class="font-medium text-n-slate-12">
            {{ t('INFORMATIONAL_BROADCAST.SCHEDULE.TITLE') }}
          </h2>
          <div class="flex flex-wrap gap-2">
            <label
              v-for="(key, day) in DAY_KEYS"
              :key="key"
              class="flex items-center gap-1 px-2 py-1 text-sm border rounded border-n-weak text-n-slate-12"
            >
              <input
                type="checkbox"
                :checked="allowedDays.includes(day)"
                @change="toggleDay(day)"
              />
              {{ dayLabels[day] }}
            </label>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <label class="text-sm text-n-slate-12">
              {{ t('INFORMATIONAL_BROADCAST.SCHEDULE.FROM') }}
              <input
                v-model="windowStart"
                type="time"
                class="w-full px-3 py-2 mt-1 border rounded-lg border-n-weak bg-n-alpha-1"
              />
            </label>
            <label class="text-sm text-n-slate-12">
              {{ t('INFORMATIONAL_BROADCAST.SCHEDULE.TO') }}
              <input
                v-model="windowEnd"
                type="time"
                class="w-full px-3 py-2 mt-1 border rounded-lg border-n-weak bg-n-alpha-1"
              />
            </label>
          </div>
        </div>
      </section>

      <section
        v-if="estimate"
        class="p-5 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <h2 class="font-medium text-n-slate-12">
          {{ t('INFORMATIONAL_BROADCAST.ESTIMATE.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{
            t('INFORMATIONAL_BROADCAST.ESTIMATE.DESCRIPTION', {
              count: preview?.eligibleCount || 0,
              date: new Intl.DateTimeFormat('pt-BR', {
                dateStyle: 'short',
                timeStyle: 'short',
                timeZone: 'America/Sao_Paulo',
              }).format(new Date(estimate.estimatedCompletion)),
            })
          }}
        </p>
      </section>

      <section
        v-if="campaign"
        class="p-5 space-y-3 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <h2 class="font-medium text-n-slate-12">
          {{ t('INFORMATIONAL_BROADCAST.STATUS.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{
            t('INFORMATIONAL_BROADCAST.STATUS.DESCRIPTION', {
              processed: campaign.processed,
              total: campaign.totalRecipients,
              sent: campaign.counts.sent,
              failed: campaign.counts.failed,
              skipped: campaign.counts.skipped,
            })
          }}
        </p>
        <div class="h-2 overflow-hidden rounded bg-n-alpha-2">
          <div
            class="h-full bg-n-brand"
            :style="{ width: `${progressPercent}%` }"
          />
        </div>
      </section>

      <p
        v-if="!loading && !preview?.eligibleCount"
        class="text-sm text-n-slate-11"
      >
        {{ t('INFORMATIONAL_BROADCAST.EMPTY') }}
      </p>

      <Button
        :label="t('INFORMATIONAL_BROADCAST.START')"
        icon="i-lucide-send"
        lg
        :is-loading="starting"
        :disabled="!isValid || starting"
        @click="startCampaign"
      />
    </div>
  </div>
</template>
